// **Die Detailseite eines Mandanten** (docs/11 10). Die Formulare sind die des
// Wizards (form-tab.tsx); gespeichert wird in die Plattform, „Übernehmen"
// bringt es in die Instanz (Management-API, und die `.env` im Wartungsfenster).
//
// Über jedem Reiter hängt die Lauf-Karte: der letzte Lauf, solange er nicht
// fertig ist (auf der Übersicht immer), und jeder, den ein Reiter anstößt.

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { keys, useTenant } from "../../api/queries";
import type { Run } from "../../api/types";
import { PageHead } from "../../app/page-head";
import { stateOf } from "../../lib/labels";
import { Link, useNavigate } from "../../router/router";
import { Badge, ErrorNote, Loading } from "../../ui/badge";
import { AuditTab } from "./audit-tab";
import { CostsTab } from "./costs-tab";
import { FormTab } from "./form-tab";
import { loginAs } from "./login-as";
import { ModelsTab } from "./models-tab";
import { OpsTab } from "./ops-tab";
import { OverviewTab } from "./overview-tab";
import { RunCard } from "./run-card";
import { FORM_TABS, TABS, type RunStart, type TabProps } from "./tabs";
import { TemplatesTab } from "./templates-tab";
import { UsersTab } from "./users-tab";

function TabContent({ tab, ...props }: TabProps & { tab: string }) {
  const step = FORM_TABS[tab];
  if (step) return <FormTab tenant={props.tenant} checks={props.checks} step={step} onRunStarted={(run) => props.onRunStarted(run)} />;
  switch (tab) {
    case "models": return <ModelsTab {...props} />;
    case "templates": return <TemplatesTab {...props} />;
    case "costs": return <CostsTab {...props} />;
    case "users": return <UsersTab {...props} />;
    case "audit": return <AuditTab {...props} />;
    case "ops": return <OpsTab {...props} />;
    default: return <OverviewTab {...props} />;
  }
}

export function DetailView({ id, tab }: { id: number; tab: string }) {
  const query = useTenant(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const runHost = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState<{ run: Run; how: RunStart } | null>(null);
  const t = query.data?.tenant;

  // Ein Entwurf hat keine Detailseite — er gehört in den Wizard, an seinen Schritt.
  useEffect(() => {
    if (t?.state === "draft") navigate(`/tenants/${id}/wizard/${Math.min(7, t.wizard_step)}`, { replace: true });
  }, [t, id, navigate]);

  useEffect(() => {
    if (started?.how.scroll) runHost.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [started]);

  const onRunStarted = useCallback((run: Run, how: RunStart = {}) => setStarted({ run, how }), []);
  const onFinished = useCallback((run: Run) => {
    if (started?.how.onFinished) { started.how.onFinished(run); return; }
    // Was früher „die Seite neu bauen" war: alles dieses Mandanten frisch holen.
    setStarted(null);
    void queryClient.invalidateQueries({ queryKey: keys.tenant(id) });
  }, [started, queryClient, id]);

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorNote error={query.error} />;
  const { tenant, run, checks } = query.data;
  if (tenant.state === "draft") return null;

  const [st, tone] = stateOf(tenant.state);
  const live = tenant.state === "active" || tenant.state === "failed";
  const shown = started?.run ?? (run && (run.state !== "done" || tab === "") ? run : null);

  return (
    <>
      <PageHead
        title={tenant.name || tenant.slug}
        badge={<Badge tone={tone}>{st}</Badge>}
        hint={<a href={`https://${tenant.host}`} target="_blank" rel="noopener">{tenant.host}</a>}
        back={["/tenants", "Mandanten"]}
        actions={live ? <button type="button" className="pf-btn" onClick={() => void loginAs(tenant.id, "admin")}>In Mandant anmelden</button> : null}
      />
      <nav className="pf-tabs">
        {TABS.map(([k, label]) => (
          <Link key={k} className={`pf-tabs__tab${k === tab ? " pf-tabs__tab--active" : ""}`} to={`/tenants/${id}${k ? `/${k}` : ""}`}>{label}</Link>
        ))}
      </nav>
      <div ref={runHost}>{shown && <RunCard key={shown.id} tenantId={id} run={shown} onFinished={onFinished} />}</div>
      <div className="pf-main">
        <TabContent tab={tab} tenant={tenant} checks={checks} onRunStarted={onRunStarted} />
      </div>
    </>
  );
}
